import { Dialog, DialogContent, DialogTitle, IconButton, Paper, Typography, Box, Chip } from "@mui/material";
import { FC } from "react";
import { MdClose } from "react-icons/md";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { MarkAsReadNotificationsUseCase } from "../../../../Usecase/Notifications/mark-as-read-notifications-usecase";
import { useStockDiscrepancy } from "./use-stock-discrepancy.hook";

interface IStockDiscrepancyItem {
  sku: string;
  mask: string;
  auditDate: string;
  productId: string;
  productName: string;
  hubFinishedGoodsQuantity: number;
  diffFinishedGoodsQuantity: number;
  hubAllocatedStockQuantity: number;
  hubWorkInProgressQuantity: number;
  omieFinishedGoodsQuantity: number;
  diffAllocatedStockQuantity: number;
  diffWorkInProgressQuantity: number;
  omieAllocatedStockQuantity: number;
  omieWorkInProgressQuantity: number;
}

interface IStockDiscrepancyModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  data: IStockDiscrepancyItem[];
  finishedGoodsDiscrepancies?: number;
  allocatedDiscrepancies?: number;
  workInProgressDiscrepancies?: number;
  notificationId: string;
  markAsReadNotificationUseCase: MarkAsReadNotificationsUseCase
}

const StockDiscrepancyModal: FC<IStockDiscrepancyModalProps> = ({
  open,
  onClose,
  title,
  data,
  finishedGoodsDiscrepancies,
  allocatedDiscrepancies,
  workInProgressDiscrepancies,
  notificationId,
  markAsReadNotificationUseCase
}) => {
  const { loading, handleMarkAsReadNotification } = useStockDiscrepancy({ notificationId, markAsReadNotificationUseCase })
  const columns: GridColDef[] = [
    { 
      field: 'sku', 
      headerName: 'SKU', 
      width: 130,
      filterable: true,
    },
    { 
      field: 'mask', 
      headerName: 'Máscara', 
      width: 200,
      filterable: true,
    },
    { 
      field: 'productName', 
      headerName: 'Produto', 
      flex: 1,
      minWidth: 400,
      filterable: true,
    },
    { 
      field: 'store', 
      headerName: 'Loja', 
      width: 50,
      filterable: true,
    },
    { 
      field: 'diffFinishedGoodsQuantity', 
      headerName: 'Diff. Acabados', 
      width: 130,
      type: 'number',
      renderCell: (params) => {
        const value = params.value as number;
        return (
          <Box sx={{ 
            color: value !== 0 ? (value > 0 ? 'success.main' : 'error.main') : 'text.primary',
            fontWeight: value !== 0 ? 600 : 400
          }}>
            {value > 0 ? `+${value}` : value}
          </Box>
        );
      }
    },
    { 
      field: 'diffAllocatedStockQuantity', 
      headerName: 'Diff. Empenho', 
      width: 130,
      type: 'number',
      renderCell: (params) => {
        const value = params.value as number;
        return (
          <Box sx={{ 
            color: value !== 0 ? (value > 0 ? 'success.main' : 'error.main') : 'text.primary',
            fontWeight: value !== 0 ? 600 : 400
          }}>
            {value > 0 ? `+${value}` : value}
          </Box>
        );
      }
    },
    { 
      field: 'diffWorkInProgressQuantity', 
      headerName: 'Diff. Em Produção', 
      width: 150,
      type: 'number',
      renderCell: (params) => {
        const value = params.value as number;
        return (
          <Box sx={{ 
            color: value !== 0 ? (value > 0 ? 'success.main' : 'error.main') : 'text.primary',
            fontWeight: value !== 0 ? 600 : 400
          }}>
            {value > 0 ? `+${value}` : value}
          </Box>
        );
      }
    },
    { 
      field: 'hubFinishedGoodsQuantity', 
      headerName: 'HUB Acabados', 
      width: 130,
      type: 'number',
    },
    { 
      field: 'omieFinishedGoodsQuantity', 
      headerName: 'OMIE Acabados', 
      width: 140,
      type: 'number',
    },
    { 
      field: 'hubAllocatedStockQuantity', 
      headerName: 'HUB Empenho', 
      width: 130,
      type: 'number',
    },
    { 
      field: 'omieAllocatedStockQuantity', 
      headerName: 'OMIE Empenho', 
      width: 140,
      type: 'number',
    },
    { 
      field: 'hubWorkInProgressQuantity', 
      headerName: 'HUB Em Produção', 
      width: 150,
      type: 'number',
    },
    { 
      field: 'omieWorkInProgressQuantity', 
      headerName: 'OMIE Em Produção', 
      width: 160,
      type: 'number',
    },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Executado em: {data[0]?.auditDate}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {finishedGoodsDiscrepancies !== undefined && (
              <Chip 
                label={`Divergências Acabados: ${finishedGoodsDiscrepancies}`} 
                color="error" 
                size="small" 
              />
            )}
            {allocatedDiscrepancies !== undefined && (
              <Chip 
                label={`Divergências Empenho: ${allocatedDiscrepancies}`} 
                color="warning" 
                size="small" 
              />
            )}
            {workInProgressDiscrepancies !== undefined && (
              <Chip 
                label={`Divergências Em Produção: ${workInProgressDiscrepancies}`} 
                color="info" 
                size="small" 
              />
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <MdClose size={24} />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ padding: '24px' }}>
        <Paper sx={{ height: 'calc(90vh - 200px)', width: '100%' }}>
          <DataGrid
            rows={data}
            columns={columns}
            getRowId={(row) => row.sku}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                fontWeight: 600,
              },
            }}
          />
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export { StockDiscrepancyModal };
export type { IStockDiscrepancyItem, IStockDiscrepancyModalProps };
